import { useEffect, useMemo, useState } from "react";
import { Button } from "../components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { toast } from "sonner";
import {
  fetchBrandTiers,
  saveBrandTiers,
  fetchCsvInfo,
  CATEGORIES,
} from "../lib/api";
import { Plus, Save, X, Search, Check, AlertCircle } from "lucide-react";

const TIERS = ["A", "B", "C", "D", "E", "F", "G", "H"];
const TIER_LABELS = {
  A: "Premium",
  B: "Mainstream",
  C: "Quality",
  D: "Value",
  E: "Economy",
  F: "Budget",
  G: "Entry",
  H: "Custom",
};

export default function BrandHierarchy() {
  return (
    <div className="space-y-6">
      {CATEGORIES.map((c) => (
        <CategoryBrandPanel key={c.value} category={c.value} categoryLabel={c.label} />
      ))}
    </div>
  );
}

function CategoryBrandPanel({ category, categoryLabel }) {
  const [tiers, setTiers] = useState({});
  const [loading, setLoading] = useState(true);
  const [csvBrands, setCsvBrands] = useState([]);
  const [brandsLoading, setBrandsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [flash, setFlash] = useState(false);

  const loaded = csvBrands.length > 0;

  const reloadBrands = () => {
    setBrandsLoading(true);
    fetchCsvInfo(category)
      .then((info) => setCsvBrands(info.brands || []))
      .catch(() => toast.error(`Failed to load brands for ${categoryLabel}`))
      .finally(() => setBrandsLoading(false));
  };

  useEffect(() => {
    reloadBrands();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  useEffect(() => {
    fetchBrandTiers(category)
      .then((t) => setTiers(t))
      .catch(() => toast.error(`Failed to load brand tiers for ${categoryLabel}`))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  // Build a set of brands assigned anywhere
  const assignedMap = useMemo(() => {
    const m = {};
    TIERS.forEach((t) => (tiers[t] || []).forEach((b) => (m[b] = t)));
    return m;
  }, [tiers]);

  const addBrand = (tier, brand) => {
    const b = String(brand).trim().toUpperCase();
    if (!b) return;
    if (!csvBrands.includes(b)) {
      toast.error(`Brand "${b}" is not in the loaded CSV`);
      return;
    }
    if (assignedMap[b]) {
      toast.error(`"${b}" is already in Tier ${assignedMap[b]}. Remove it first.`);
      return;
    }
    setTiers((t) => ({ ...t, [tier]: [...(t[tier] || []), b] }));
    setDirty(true);
  };

  const removeBrand = (tier, brand) => {
    setTiers((t) => ({ ...t, [tier]: (t[tier] || []).filter((x) => x !== brand) }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const saved = await saveBrandTiers(tiers, category);
      setTiers(saved);
      setDirty(false);
      setFlash(true);
      setTimeout(() => setFlash(false), 2500);
      toast.success(`${categoryLabel} brand tiers saved`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const totalAssigned = Object.keys(assignedMap).length;

  return (
    <section className="border border-[#E2E8F0] bg-white sharp">
      <div className="border-b border-[#E2E8F0] bg-[#F8F9FA] px-6 py-3 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow text-[#64748B]">{categoryLabel} — Brand Tier Classification</div>
          <div className="text-xs text-[#64748B] mt-1">
            Classify brands from this category's CSV across 8 tiers. Each brand can only belong to one tier.
          </div>
        </div>
        <div className="flex items-center gap-2">
          {flash && (
            <span className="text-[12px] text-[#16A34A] flex items-center gap-1 slide-in font-semibold">
              <Check size={14} /> Saved
            </span>
          )}
          <Button
            size="sm"
            onClick={handleSave}
            disabled={!dirty || saving}
            className="rounded-sm h-8 text-xs gap-2 bg-[#1A2340] hover:bg-[#253060] text-white disabled:opacity-40"
            data-testid={`save-brands-button-${category}`}
          >
            <Save size={13} /> {saving ? "Saving…" : "Save Brands"}
          </Button>
        </div>
      </div>

      <div className="px-6 py-4 border-b border-[#E2E8F0] bg-white flex items-center gap-6 flex-wrap text-[12px]">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-[#F5C800] sharp" />
          <span className="eyebrow text-[#64748B]">CSV Brands</span>
          <span className="font-mono-data font-bold text-[#1A2340]" data-testid={`csv-brands-count-${category}`}>{csvBrands.length}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-[#1A2340] sharp" />
          <span className="eyebrow text-[#64748B]">Assigned</span>
          <span className="font-mono-data font-bold text-[#1A2340]" data-testid={`assigned-count-${category}`}>{totalAssigned}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-[#E2E8F0] sharp" />
          <span className="eyebrow text-[#64748B]">Unassigned</span>
          <span className="font-mono-data font-bold text-[#1A2340]" data-testid={`unassigned-count-${category}`}>
            {Math.max(0, csvBrands.length - totalAssigned)}
          </span>
        </div>
      </div>

      {!brandsLoading && !loaded && (
        <div className="m-6 p-4 border border-amber-200 bg-amber-50 flex items-start gap-3">
          <AlertCircle className="text-amber-600 flex-shrink-0 mt-0.5" size={18} />
          <div className="text-sm">
            <div className="font-semibold text-[#1A2340]">No CSV loaded for {categoryLabel}</div>
            <div className="text-[#64748B] mt-1">
              Load its source file in the Execution Center, then{" "}
              <button onClick={reloadBrands} className="underline font-semibold text-[#1A2340]" data-testid={`reload-from-brands-${category}`}>rescan</button>.
              You can only assign brands that exist in the loaded CSV.
            </div>
          </div>
        </div>
      )}

      <div className="p-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {loading ? (
          <div className="text-sm text-[#64748B]">Loading…</div>
        ) : (
          TIERS.map((tier) => (
            <TierCard
              key={tier}
              category={category}
              tier={tier}
              brands={tiers[tier] || []}
              csvBrands={csvBrands}
              assignedMap={assignedMap}
              onAdd={(b) => addBrand(tier, b)}
              onRemove={(b) => removeBrand(tier, b)}
              loaded={loaded}
            />
          ))
        )}
      </div>
    </section>
  );
}

function TierCard({ category, tier, brands, csvBrands, assignedMap, onAdd, onRemove, loaded }) {
  const [open, setOpen] = useState(false);

  // Available = brands in CSV not assigned anywhere (or assigned to this tier already)
  const available = useMemo(() => {
    return csvBrands.filter((b) => !assignedMap[b]);
  }, [csvBrands, assignedMap]);

  return (
    <div className="border border-[#E2E8F0] bg-white sharp" data-testid={`tier-card-${category}-${tier}`}>
      <div className="px-4 py-3 border-b border-[#E2E8F0] bg-[#F8F9FA] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={`tier-${tier} sharp px-2 py-1 font-display font-black text-[13px] leading-none`}>
            {tier}
          </span>
          <span className="font-semibold text-[13px] text-[#1A2340]">{TIER_LABELS[tier]}</span>
        </div>
        <span className="font-mono-data text-[11px] text-[#64748B]">{brands.length}</span>
      </div>

      <div className="p-3 space-y-3 min-h-[140px]">
        <div className="flex flex-wrap gap-1.5 min-h-[28px]">
          {brands.length === 0 ? (
            <span className="text-[11px] text-[#94a3b8] italic">No brands assigned</span>
          ) : (
            brands.map((b) => (
              <span
                key={b}
                className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#1A2340] text-white text-[11px] font-mono-data sharp"
                data-testid={`brand-tag-${category}-${tier}-${b}`}
              >
                {b}
                <button
                  onClick={() => onRemove(b)}
                  className="hover:bg-white/20 -mr-1 px-1"
                  data-testid={`remove-brand-${category}-${tier}-${b}`}
                  aria-label={`Remove ${b}`}
                >
                  <X size={10} />
                </button>
              </span>
            ))
          )}
        </div>

        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              disabled={!loaded || available.length === 0}
              className="w-full justify-start rounded-sm h-8 text-xs gap-2 border-dashed border-[#1A2340]/30 hover:bg-[#1A2340] hover:text-white disabled:opacity-40"
              data-testid={`add-brand-button-${category}-${tier}`}
            >
              <Plus size={13} />
              {available.length === 0
                ? loaded ? "All brands assigned" : "Load CSV first"
                : `Add brand (${available.length} available)`}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-0 rounded-sm border-[#1A2340]/20" align="start">
            <Command className="rounded-sm">
              <CommandInput
                placeholder="Search brands from CSV…"
                className="h-9"
                data-testid={`brand-search-${category}-${tier}`}
              />
              <CommandList>
                <CommandEmpty>
                  <div className="py-4 text-center text-xs text-[#64748B]">
                    <Search size={14} className="mx-auto mb-2 opacity-50" />
                    No matching brand in CSV
                  </div>
                </CommandEmpty>
                <CommandGroup heading={`${available.length} brands available`}>
                  {available.map((b) => (
                    <CommandItem
                      key={b}
                      value={b}
                      onSelect={() => {
                        onAdd(b);
                        setOpen(false);
                      }}
                      className="font-mono-data text-[12px] rounded-none cursor-pointer"
                      data-testid={`brand-option-${category}-${tier}-${b}`}
                    >
                      <Plus size={12} className="mr-2 opacity-60" /> {b}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}
